// @givmo/board — the officer's acts on an open year: remind, send to someone who joined, change the
// due date, withdraw, and close (CBG-YR2-WEB1; frames o4-remind, o5-close).
//
// ══ EVERY ACT SAYS WHAT WILL HAPPEN BEFORE IT HAPPENS ═══════════════════════════════════════════
//
// Reminding and sending show the email as it will be sent; closing says what the closing record holds
// (who was asked, and who submitted and when — no one's interests) and that late answers are still
// taken; withdrawing says it writes nothing. Each act is the backend's, with its own refusals (a
// reminder inside a day, a year no longer open), and a refusal shows the backend's own sentence.
// Reminders go only when an officer presses Remind: nothing here schedules anything (Q6).

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  isGivmoError,
  type BoardCoiCycleOut,
  type BoardCoiCyclePersonOut,
} from '@givmo/api-client';
import { Button, ErrorMessage, TextField, toast } from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { useBoardOperations } from '../../board-runtime';
import { capacityOf, isPastDue, mailDate, outstandingNames, shortDay } from './coi-decode';
import { DialogFrame, EmailPreview, PersonCell } from './coi-parts';
import { useRefreshCoi } from './coi-queries';
import { coiWords as w } from './coi-words';

const SECONDARY = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';

function failure(caught: unknown): string {
  return isGivmoError(caught) && caught.message.trim().length > 0 ? caught.message : w.couldNotSave;
}

/** The email's lines as the backend's template renders them for this person. */
export function emailLines(
  act: 'request' | 'reminder',
  {
    name,
    organization,
    dueOn,
    joined,
    joinedAfterCloseLabel = null,
  }: {
    name: string;
    organization: string;
    dueOn: string;
    joined: boolean;
    /** CBG-YR4-COI-JOINER: the closed year's label, sent to someone who joined after it closed. */
    joinedAfterCloseLabel?: string | null;
  },
): string[] {
  const due = mailDate(dueOn);
  const ask =
    act === 'request'
      ? `${w.emailAsk(organization)} ${
          joinedAfterCloseLabel !== null
            ? w.emailJoinedAfterClose(joinedAfterCloseLabel)
            : w.emailPleaseSendBy(due)
        }`
      : `${w.emailReminderAsk(organization)} ${isPastDue(dueOn) ? w.emailWasDue(due) : w.emailYoursDue(due)}`;
  return [w.emailHello(name), ask, ...(joined ? [] : [w.emailNotJoined(organization)])];
}

// ── Remind (o4-remind) ─────────────────────────────────────────────────────────────────────────────

export function RemindDialog({
  orgRef,
  cycle,
  people,
  organization,
  onClose,
}: {
  orgRef: string;
  cycle: BoardCoiCycleOut;
  /** Who this reminder goes to: everyone the page read as remindable, or the one person a row named. */
  people: BoardCoiCyclePersonOut[];
  organization: string;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const refresh = useRefreshCoi(orgRef);
  const [error, setError] = useState<string | null>(null);
  const names = people.map((person) => person.display_name ?? '');
  // The email as most people get it: someone who has joined Board, else the first person named.
  const first = people.find((person) => person.joined_board) ?? people[0];
  const named = people.length === 1;

  const remind = useMutation({
    mutationFn: async () => {
      const { data } = await operations.officerRemindBoardCoiCycleRecipients({
        path: { org_ref: orgRef, cycle_id: cycle.id },
        // Exactly the people this dialog names, never `null` ("everyone it can reach now"): someone
        // who became remindable after the page read gets no mail the officer didn't see named, and
        // the backend refuses the whole send if anyone named no longer can be (all-or-nothing).
        body: { board_member_ids: people.map((person) => person.board_member_id) },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: async (data) => {
      toast.success(
        w.remindersSent(typeof data?.emails_sent === 'number' ? data.emails_sent : people.length),
      );
      await refresh();
      onClose();
    },
    onError: (caught) => setError(failure(caught)),
  });

  return (
    <DialogFrame
      title={w.remindTitle(names)}
      testId="coi-remind-dialog"
      onClose={onClose}
      footer={
        <>
          <Button appearance="saas" tone="secondary" onClick={onClose} testId="coi-remind-cancel">
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={remind.isPending}
            onClick={() => {
              setError(null);
              remind.mutate();
            }}
            testId="coi-remind-send"
          >
            {w.sendReminders(people.length)}
          </Button>
        </>
      }
    >
      <p className={SECONDARY}>
        {named ? w.remindLeadOne(cycle.label) : w.remindLead(cycle.label)}
      </p>
      <ul className="flex flex-col divide-y divide-border-subtle rounded-md border border-border-subtle">
        {people.map((person) => (
          <li
            key={person.board_member_id}
            className="flex items-start justify-between gap-12 px-12 py-10"
            data-testid={`coi-remind-${person.board_member_id}`}
          >
            <PersonCell
              name={person.display_name ?? ''}
              capacity={capacityOf(person)}
              note={person.joined_board ? undefined : w.includesInvitation}
            />
            <span className="shrink-0 font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
              {person.last_reminded_at
                ? w.lastReminded(shortDay(person.last_reminded_at))
                : person.first_sent_at
                  ? w.sentOn(shortDay(person.first_sent_at))
                  : null}
            </span>
          </li>
        ))}
      </ul>
      {first !== undefined ? (
        <EmailPreview
          testId="coi-remind-email"
          organization={organization}
          subject={w.emailReminderSubject(cycle.label)}
          lines={emailLines('reminder', {
            name: first.display_name ?? '',
            organization,
            dueOn: cycle.due_on,
            joined: first.joined_board,
          })}
        />
      ) : null}
      {error !== null ? <ErrorMessage testId="coi-remind-error">{error}</ErrorMessage> : null}
    </DialogFrame>
  );
}

// ── Send a closed year to everyone who joined after it closed (CBG-YR4-COI-JOINER; not drawn) ──

export function SendJoinersDialog({
  orgRef,
  cycle,
  onClose,
}: {
  orgRef: string;
  cycle: BoardCoiCycleOut;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const refresh = useRefreshCoi(orgRef);
  const [error, setError] = useState<string | null>(null);
  const count = cycle.joiners_sendable_count ?? 0;

  const send = useMutation({
    mutationFn: async () => {
      // `null` names everyone the closed year can go to: its joiners after close (§22.6).
      await operations.officerSendBoardCoiCycleRequests({
        path: { org_ref: orgRef, cycle_id: cycle.id },
        body: { board_member_ids: null },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      toast.success(w.sentToJoiners);
      await refresh();
      onClose();
    },
    onError: (caught) => setError(failure(caught)),
  });

  return (
    <DialogFrame
      title={w.sendJoinersTitle(cycle.label)}
      testId="coi-send-joiners-dialog"
      onClose={onClose}
      footer={
        <>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={onClose}
            testId="coi-send-joiners-cancel"
          >
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={send.isPending}
            onClick={() => {
              setError(null);
              send.mutate();
            }}
            testId="coi-send-joiners-confirm"
          >
            {w.sendButton}
          </Button>
        </>
      }
    >
      <p className={SECONDARY}>{w.sendJoinersLead(count)}</p>
      {error !== null ? <ErrorMessage testId="coi-send-joiners-error">{error}</ErrorMessage> : null}
    </DialogFrame>
  );
}

// ── Send to someone who joined after the year started (not drawn) ─────────────────────────────────

export function SendDialog({
  orgRef,
  cycle,
  person,
  organization,
  onClose,
}: {
  orgRef: string;
  cycle: BoardCoiCycleOut;
  person: BoardCoiCyclePersonOut;
  organization: string;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const refresh = useRefreshCoi(orgRef);
  const [error, setError] = useState<string | null>(null);
  const name = person.display_name ?? '';

  const send = useMutation({
    mutationFn: async () => {
      await operations.officerSendBoardCoiCycleRequests({
        path: { org_ref: orgRef, cycle_id: cycle.id },
        body: { board_member_ids: [person.board_member_id] },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      toast.success(w.sentTo(name));
      await refresh();
      onClose();
    },
    onError: (caught) => setError(failure(caught)),
  });

  return (
    <DialogFrame
      title={w.sendTitle(name)}
      testId="coi-send-dialog"
      onClose={onClose}
      footer={
        <>
          <Button appearance="saas" tone="secondary" onClick={onClose} testId="coi-send-cancel">
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={send.isPending}
            onClick={() => {
              setError(null);
              send.mutate();
            }}
            testId="coi-send-confirm"
          >
            {w.sendButton}
          </Button>
        </>
      }
    >
      <p className={SECONDARY}>
        {person.joined_after_close
          ? w.sendLeadJoinedAfterClose(cycle.label)
          : w.sendLead(cycle.label)}
      </p>
      <EmailPreview
        testId="coi-send-email"
        organization={organization}
        subject={w.emailSubject(cycle.label)}
        lines={emailLines('request', {
          name,
          organization,
          dueOn: cycle.due_on,
          joined: person.joined_board,
          joinedAfterCloseLabel: person.joined_after_close ? cycle.label : null,
        })}
      />
      {error !== null ? <ErrorMessage testId="coi-send-error">{error}</ErrorMessage> : null}
    </DialogFrame>
  );
}

// ── Close (o5-close) ──────────────────────────────────────────────────────────────────────────────

export function CloseDialog({
  orgRef,
  cycle,
  onClose,
}: {
  orgRef: string;
  cycle: BoardCoiCycleOut;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const refresh = useRefreshCoi(orgRef);
  const [error, setError] = useState<string | null>(null);

  const close = useMutation({
    mutationFn: async () => {
      await operations.officerCloseBoardCoiCycle({
        path: { org_ref: orgRef, cycle_id: cycle.id },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      toast.success(w.closed(cycle.label));
      await refresh();
      onClose();
    },
    onError: (caught) => setError(failure(caught)),
  });

  return (
    <DialogFrame
      title={w.closeTitle(cycle.label)}
      testId="coi-close-dialog"
      width={480}
      onClose={onClose}
      footer={
        <>
          <Button appearance="saas" tone="secondary" onClick={onClose} testId="coi-close-cancel">
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={close.isPending}
            onClick={() => {
              setError(null);
              close.mutate();
            }}
            testId="coi-close-confirm"
          >
            {w.closeAndRecord}
          </Button>
        </>
      }
    >
      <p className="font-sans text-s14 font-w600 leading-h15 tracking-givmo text-text-strong">
        {w.closeCount(cycle.submitted_count, cycle.people_count, outstandingNames(cycle.people))}
      </p>
      <p className={SECONDARY}>{w.closeRecord}</p>
      <p className={SECONDARY}>{w.closeLate}</p>
      {error !== null ? <ErrorMessage testId="coi-close-error">{error}</ErrorMessage> : null}
    </DialogFrame>
  );
}

// ── The due date (not drawn) ──────────────────────────────────────────────────────────────────────

export function DueDateDialog({
  orgRef,
  cycle,
  onClose,
}: {
  orgRef: string;
  cycle: BoardCoiCycleOut;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const refresh = useRefreshCoi(orgRef);
  const [dueOn, setDueOn] = useState(cycle.due_on);
  const [error, setError] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: async () => {
      await operations.officerUpdateBoardCoiCycle({
        path: { org_ref: orgRef, cycle_id: cycle.id },
        body: { due_on: dueOn },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      toast.success(w.dueChanged);
      await refresh();
      onClose();
    },
    onError: (caught) => setError(failure(caught)),
  });

  return (
    <DialogFrame
      title={w.dueTitle}
      testId="coi-due-dialog"
      width={440}
      onClose={onClose}
      footer={
        <>
          <Button appearance="saas" tone="secondary" onClick={onClose} testId="coi-due-cancel">
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={change.isPending}
            disabled={dueOn.length === 0 || dueOn === cycle.due_on}
            onClick={() => {
              setError(null);
              change.mutate();
            }}
            testId="coi-due-save"
          >
            {w.dueSave}
          </Button>
        </>
      }
    >
      <TextField
        label={w.dueField}
        type="date"
        value={dueOn}
        onChange={(event) => setDueOn(event.target.value)}
        testId="coi-due-date"
      />
      <FieldNote>{w.dueNote}</FieldNote>
      {error !== null ? <ErrorMessage testId="coi-due-error">{error}</ErrorMessage> : null}
    </DialogFrame>
  );
}

// ── Withdraw, before anyone has answered (not drawn) ──────────────────────────────────────────────

export function WithdrawDialog({
  orgRef,
  cycle,
  onClose,
}: {
  orgRef: string;
  cycle: BoardCoiCycleOut;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const refresh = useRefreshCoi(orgRef);
  const [error, setError] = useState<string | null>(null);

  const withdraw = useMutation({
    mutationFn: async () => {
      await operations.officerCancelBoardCoiCycle({
        path: { org_ref: orgRef, cycle_id: cycle.id },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      toast.success(w.withdrawn(cycle.label));
      await refresh();
      onClose();
    },
    onError: (caught) => setError(failure(caught)),
  });

  return (
    <DialogFrame
      title={w.withdrawTitle(cycle.label)}
      testId="coi-withdraw-dialog"
      width={480}
      onClose={onClose}
      footer={
        <>
          <Button appearance="saas" tone="secondary" onClick={onClose} testId="coi-withdraw-cancel">
            {w.withdrawKeep}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={withdraw.isPending}
            onClick={() => {
              setError(null);
              withdraw.mutate();
            }}
            testId="coi-withdraw-confirm"
          >
            {w.withdrawConfirm}
          </Button>
        </>
      }
    >
      <p className={SECONDARY}>{w.withdrawLead}</p>
      {error !== null ? <ErrorMessage testId="coi-withdraw-error">{error}</ErrorMessage> : null}
    </DialogFrame>
  );
}
